import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import './authlytics_dashboard.css';

// # Simple Coming Soon component for Analytics Dashboard
const AuthlyticsDashboard = () => {
  // # State management for users and selected profile
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  // # Effect to handle body scroll lock when modal is open
  useEffect(() => {
    if (selectedUser) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // # Cleanup function
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [selectedUser]);

  // # Fetch users data on component mount
  useEffect(() => {
    fetchUsersData();
  }, []);

  // # Function to fetch users data
  const fetchUsersData = async () => {
    try {
      setLoading(true);
      // # Get current timestamp for last 48h calculation
      const now = new Date();
      const last48Hours = new Date(now.getTime() - (48 * 60 * 60 * 1000));
      
      // # Get current month key for querying
      const currentMonthKey = `${now.getFullYear()}_${(now.getMonth() + 1).toString().padStart(2, '0')}`;
      
      // # Get users collection
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      
      // # Get activities collection
      const activitiesRef = collection(db, 'activities');
      const activitiesSnapshot = await getDocs(activitiesRef);
      
      // # Create activities map for quick lookup
      const activitiesMap = new Map();
      activitiesSnapshot.docs.forEach(doc => {
        activitiesMap.set(doc.id, doc.data());
      });
      
      // # Process each user's data
      const usersData = await Promise.all(usersSnapshot.docs.map(async doc => {
        const userData = doc.data();
        const activityData = activitiesMap.get(doc.id) || {};
        const currentMonthData = activityData[currentMonthKey]?.auth_history || {};
        
        // # Calculate login and logout counts in last 48h
        const recent48hLogins = Object.values(currentMonthData.sign_ins || {})
          .filter(login => new Date(login.timestamp) > last48Hours).length;
        
        const recent48hLogouts = Object.values(currentMonthData.sign_outs || {})
          .filter(logout => new Date(logout.timestamp) > last48Hours).length;
        
        // # Get latest session
        const latestSignIn = Object.entries(currentMonthData.sign_ins || {})
          .sort(([,a], [,b]) => new Date(b.timestamp) - new Date(a.timestamp))[0];
        
        const latestSignOut = Object.entries(currentMonthData.sign_outs || {})
          .sort(([,a], [,b]) => new Date(b.timestamp) - new Date(a.timestamp))[0];
        
        // # Get current active devices
        const activeDevices = activityData.devices || {};

        // # Helper function to check if a device has an active session
        const hasActiveSession = (deviceId, signIns, signOuts) => {
          // # Get all sign-ins for this device
          const deviceSignIns = Object.values(signIns || {})
            .filter(signIn => signIn.device_id === deviceId)
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

          // # Get all sign-outs for this device
          const deviceSignOuts = Object.values(signOuts || {})
            .filter(signOut => signOut.device_id === deviceId)
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

          // # Check if there's a sign-in without a matching sign-out
          return deviceSignIns.some(signIn => {
            const signInTime = new Date(signIn.timestamp);
            return !deviceSignOuts.some(signOut => 
              signOut.device_id === signIn.device_id &&
              Number(signOut.hardware_concurrency) === Number(signIn.hardware_concurrency) &&
              signOut.device.split(' - ')[0] === signIn.device.split(' - ')[0] &&
              new Date(signOut.timestamp) > signInTime
            );
          });
        };
        
        // # Determine if user is active based on device sessions
        const isActive = Object.keys(activeDevices).some(deviceKey => {
          const device = activeDevices[deviceKey];
          return hasActiveSession(device.device_id, currentMonthData.sign_ins, currentMonthData.sign_outs);
        });
        
        // # Helper function to format device info
        const formatDeviceInfo = (deviceInfo) => {
          const userAgent = deviceInfo.device_name;
          let deviceType = 'Unknown Device';
          let browser = 'Unknown Browser';
          let os = 'Unknown OS';
          let version = '';

          // # Detect OS
          if (userAgent.includes('Windows')) {
            os = 'Windows';
            if (userAgent.includes('Windows NT 10.0')) os = 'Windows 10/11';
            else if (userAgent.includes('Windows NT 6.3')) os = 'Windows 8.1';
            else if (userAgent.includes('Windows NT 6.2')) os = 'Windows 8';
            else if (userAgent.includes('Windows NT 6.1')) os = 'Windows 7';
          } else if (userAgent.includes('Mac OS X')) {
            os = 'macOS';
            const match = userAgent.match(/Mac OS X ([0-9._]+)/);
            if (match) {
              version = match[1].replace(/_/g, '.');
              os = `macOS ${version}`;
            }
          } else if (userAgent.includes('Android')) {
            os = 'Android';
          } else if (userAgent.includes('Linux')) {
            os = 'Linux';
            if (userAgent.includes('Ubuntu')) os = 'Ubuntu Linux';
            else if (userAgent.includes('Fedora')) os = 'Fedora Linux';
            else if (userAgent.includes('SUSE')) os = 'SUSE Linux';
          } else if (userAgent.includes('iPhone')) {
            os = 'iOS';
          }

          // # Detect browser
          if (userAgent.includes('Firefox/')) {
            browser = 'Firefox';
            const match = userAgent.match(/Firefox\/([0-9.]+)/);
            if (match) version = match[1];
          } else if (userAgent.includes('Chrome/')) {
            browser = 'Chrome';
            const match = userAgent.match(/Chrome\/([0-9.]+)/);
            if (match) version = match[1];
          } else if (userAgent.includes('Safari/')) {
            browser = 'Safari';
            const match = userAgent.match(/Version\/([0-9.]+)/);
            if (match) version = match[1];
          } else if (userAgent.includes('Edge/')) {
            browser = 'Edge';
            const match = userAgent.match(/Edge\/([0-9.]+)/);
            if (match) version = match[1];
          }

          // # Determine device type
          if (userAgent.includes('Mobile') || userAgent.includes('iPhone') || userAgent.includes('Android')) {
            deviceType = 'Mobile Device';
          } else if (userAgent.includes('Tablet') || userAgent.includes('iPad')) {
            deviceType = 'Tablet';
          } else {
            deviceType = 'Desktop';
          }

          return {
            deviceType,
            os,
            browser: version ? `${browser} ${version.split('.')[0]}` : browser,
            fullDetails: `${os} · ${browser}${version ? ` ${version.split('.')[0]}` : ''}`
          };
        };

        // # Format devices with the helper function
        const formattedDevices = Object.values(activeDevices).map(device => {
          const formattedInfo = formatDeviceInfo(device);
          const deviceHasActiveSession = hasActiveSession(
            device.device_id, 
            currentMonthData.sign_ins, 
            currentMonthData.sign_outs
          );

          return {
            deviceId: device.device_id,
            deviceName: formattedInfo.fullDetails,
            deviceType: formattedInfo.deviceType,
            os: formattedInfo.os,
            browser: formattedInfo.browser,
            lastSeen: device.last_seen,
            isActive: deviceHasActiveSession,
            displayString: `${formattedInfo.deviceType} · ${formattedInfo.fullDetails} (${deviceHasActiveSession ? 'Active' : 'Inactive'})`
          };
        });

        return {
          uid: doc.id,
          // # Basic user info from users collection
          profilePic: userData.photoURL || '/default-avatar.png',
          name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Unknown User',
          email: userData.email || 'No email',
          role: userData.role || 'user',
          university: userData.university || 'Not specified',
          phase: userData.phase || 'Not specified',
          
          // # Activity data
          recentLogins: recent48hLogins,
          recentLogouts: recent48hLogouts,
          status: isActive ? 'active' : 'inactive',
          lastSignInTime: userData.metadata?.lastSignInTime || null,
          lastSessionStart: latestSignIn ? latestSignIn[1].timestamp : null,
          lastSessionEnd: latestSignOut ? latestSignOut[1].timestamp : null,
          activeDevices: formattedDevices,
          totalLogins: Object.keys(currentMonthData.sign_ins || {}).length,
          totalDevices: new Set([
            ...Object.values(currentMonthData.sign_ins || {}).map(s => s.device_id),
            ...Object.values(currentMonthData.sign_outs || {}).map(s => s.device_id)
          ]).size,
          
          // # Additional metadata
          createdAt: userData.metadata?.createdAt || null,
          isProfileComplete: userData.isProfileComplete || false
        };
      }));
      
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users data:', error);
    } finally {
      setLoading(false);
    }
  };

  // # Handle profile click
  const handleProfileClick = (user) => {
    setSelectedUser(user);
  };

  // # Handle modal close
  const handleModalClose = () => {
    setSelectedUser(null);
  };

  // # Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && selectedUser) {
        handleModalClose();
      }
    };

    if (selectedUser) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [selectedUser]);

  // # Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <div className="auth_analytics_dashboard-container">
        {/* # Search input */}
        <div className="auth_analytics_search-container">
          <input
            type="text"
            className="auth_analytics_search-input"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* # Loading state */}
        {loading ? (
          <div className="auth_analytics_loading">Loading user data...</div>
        ) : (
          <div className="auth_analytics_table-container">
            <div className="auth_analytics_table-header">
              <div className="auth_analytics_col-profile">Profile</div>
              <div className="auth_analytics_col-name">Name</div>
              <div className="auth_analytics_col-email">Email</div>
              <div className="auth_analytics_col-stat">In/Out 48h</div>
              <div className="auth_analytics_col-status">Status</div>
              <div className="auth_analytics_col-datetime">Session Start</div>
              <div className="auth_analytics_col-devices">Active Devices</div>
            </div>

            {filteredUsers.map(user => (
              <div key={user.uid} className="auth_analytics_user-row">
                <div className="auth_analytics_col-profile">
                  <img
                    src={user.profilePic}
                    alt={user.name}
                    className="auth_analytics_user-avatar"
                    onClick={() => handleProfileClick(user)}
                  />
                </div>
                <div className="auth_analytics_col-name">{user.name}</div>
                <div className="auth_analytics_col-email">{user.email}</div>
                <div className="auth_analytics_col-stat">
                  <div className="auth_analytics_stat-value">
                    <span className="auth_analytics_stat-in">{user.recentLogins}</span>
                    <span className="auth_analytics_stat-separator">/</span>
                    <span className="auth_analytics_stat-out">{user.recentLogouts}</span>
                  </div>
                  <div className="auth_analytics_stat-label">In/Out 48h</div>
                </div>
                <div className="auth_analytics_col-status">
                  <span className={`auth_analytics_status-indicator ${user.status}`}>
                    {user.status}
                  </span>
                </div>
                <div className="auth_analytics_col-datetime">
                  {user.lastSessionStart ? new Date(user.lastSessionStart).toLocaleString() : 'N/A'}
                </div>
                <div className="auth_analytics_col-devices">
                  <ul className="auth_analytics_device-list">
                    {user.activeDevices.map((device, index) => (
                      <li key={index} title={device.displayString}>
                        <span className="auth_analytics_device-type">
                          <i className={`fas fa-${device.deviceType.toLowerCase() === 'mobile device' ? 'mobile-alt' : 
                                        device.deviceType.toLowerCase() === 'tablet' ? 'tablet-alt' : 'desktop'}`}></i>
                          {device.deviceType}
                        </span>
                        <span className={`auth_analytics_device-status ${device.isActive ? 'active' : 'inactive'}`}>
                          {device.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* # User Profile Modal */}
      {selectedUser && (
        <div className="auth_analytics_modal" onClick={handleModalClose}>
          <div className="auth_analytics_modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="auth_analytics_modal-close" onClick={handleModalClose}>&times;</button>
            <div className="auth_analytics_modal-header">
              <img src={selectedUser.profilePic} alt={selectedUser.name} className="auth_analytics_modal-avatar" />
              <h2>{selectedUser.name}</h2>
              <p>{selectedUser.email}</p>
              <div className={`auth_analytics_status-indicator ${selectedUser.status}`}>
                {selectedUser.status}
              </div>
            </div>
            <div className="auth_analytics_modal-body">
              <div className="auth_analytics_modal-section">
                <h3>Basic Information</h3>
                <div className="auth_analytics_modal-stat">
                  <span className="auth_analytics_modal-label">Role</span>
                  <span className="auth_analytics_modal-value">{selectedUser.role || 'Not specified'}</span>
                </div>
                <div className="auth_analytics_modal-stat">
                  <span className="auth_analytics_modal-label">University</span>
                  <span className="auth_analytics_modal-value">{selectedUser.university || 'Not specified'}</span>
                </div>
                <div className="auth_analytics_modal-stat">
                  <span className="auth_analytics_modal-label">Phase</span>
                  <span className="auth_analytics_modal-value">{selectedUser.phase || 'Not specified'}</span>
                </div>
              </div>

              <div className="auth_analytics_modal-section">
                <h3>Activity Statistics</h3>
                <div className="auth_analytics_modal-stat">
                  <span className="auth_analytics_modal-label">Recent Activity (48h)</span>
                  <span className="auth_analytics_modal-value">
                    <span className="auth_analytics_stat-in">{selectedUser.recentLogins}</span>
                    <span className="auth_analytics_stat-separator"> / </span>
                    <span className="auth_analytics_stat-out">{selectedUser.recentLogouts}</span>
                    <span className="auth_analytics_stat-label-small"> (Sign-ins / Sign-outs)</span>
                  </span>
                </div>
                <div className="auth_analytics_modal-stat">
                  <span className="auth_analytics_modal-label">Total Monthly Logins</span>
                  <span className="auth_analytics_modal-value">
                    {selectedUser.totalLogins} {selectedUser.totalLogins === 1 ? 'login' : 'logins'}
                  </span>
                </div>
                <div className="auth_analytics_modal-stat">
                  <span className="auth_analytics_modal-label">Different Devices</span>
                  <span className="auth_analytics_modal-value">
                    {selectedUser.totalDevices} {selectedUser.totalDevices === 1 ? 'device' : 'devices'} this month
                  </span>
                </div>
              </div>

              <div className="auth_analytics_modal-section">
                <h3>Session Information</h3>
                <div className="auth_analytics_modal-stat">
                  <span className="auth_analytics_modal-label">Last Session Start</span>
                  <span className="auth_analytics_modal-value">
                    {selectedUser.lastSessionStart ? 
                      new Date(selectedUser.lastSessionStart).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      }) 
                      : 'Not available'}
                  </span>
                </div>
                <div className="auth_analytics_modal-stat">
                  <span className="auth_analytics_modal-label">Last Session End</span>
                  <span className="auth_analytics_modal-value">
                    {selectedUser.lastSessionEnd ? 
                      new Date(selectedUser.lastSessionEnd).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit'
                      }) 
                      : 'Session in progress'}
                  </span>
                </div>
              </div>

              <div className="auth_analytics_modal-section">
                <h3>Account Details</h3>
                <div className="auth_analytics_modal-stat">
                  <span className="auth_analytics_modal-label">Account Created</span>
                  <span className="auth_analytics_modal-value">
                    {selectedUser.createdAt ? 
                      new Date(selectedUser.createdAt).toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) 
                      : 'Not available'}
                  </span>
                </div>
                <div className="auth_analytics_modal-stat">
                  <span className="auth_analytics_modal-label">Profile Status</span>
                  <span className="auth_analytics_modal-value">
                    <span className={`auth_analytics_status-indicator ${selectedUser.isProfileComplete ? 'active' : 'inactive'}`}>
                      {selectedUser.isProfileComplete ? 'Complete' : 'Incomplete'}
                    </span>
                  </span>
                </div>
              </div>

              <div className="auth_analytics_modal-section">
                <h3>Active Devices</h3>
                <ul className="auth_analytics_device-list">
                  {selectedUser.activeDevices.map((device, index) => (
                    <li key={index} title={device.displayString}>
                      <div className="auth_analytics_device-info">
                        <span className="auth_analytics_device-type">
                          <i className={`fas fa-${device.deviceType.toLowerCase() === 'mobile device' ? 'mobile-alt' : 
                                      device.deviceType.toLowerCase() === 'tablet' ? 'tablet-alt' : 'desktop'}`}></i>
                          {device.deviceType}
                        </span>
                        <span className="auth_analytics_device-os">{device.os}</span>
                        <span className="auth_analytics_device-browser">{device.browser}</span>
                        <span className={`auth_analytics_device-status ${device.isActive ? 'active' : 'inactive'}`}>
                          {device.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <span className="auth_analytics_device-details">
                        Last seen: {new Date(device.lastSeen).toLocaleString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuthlyticsDashboard;
